import Echo from 'laravel-echo'
import { storeToRefs } from 'pinia'
import Pusher from 'pusher-js'
import { onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useUserInboxStore } from '@/stores/userInbox'
import { type ResourceUpdatedPayload, dispatchResourceUpdatedSideEffects } from '@/utils/resourceUpdatedBroadcast'
import axios from '@axios'

let echoClient: Echo<'reverb'> | null = null
let activeProgramChannelUuid: string | null = null
let activePatientCaseChannelUuid: string | null = null

/** Cancels stale async `syncEcho()` when login/uuid watchers fire again (e.g. refreshData). */
let syncEchoRunId = 0

/** Payload for `inbox.updated` — signal-only; list rows come from REST. */
interface InboxUpdatedPayload {
  v: number
  automation_event_type?: string
}

function debugEcho(...args: unknown[]): void {
  if (import.meta.env.DEV)
    console.info('[vector:echo]', ...args)
}

/** Sanctum CSRF before Echo private-channel auth (avoids 419 on cross-origin API). */
async function ensureCsrfCookieForEcho(serverBaseUrl: string): Promise<void> {
  await axios.get('/sanctum/csrf-cookie', { baseURL: serverBaseUrl })
}

/** Route broadcasting auth through axios so withXSRFToken + 419 retry apply (Echo's fetch does not). */
function createEchoChannelAuthorizer() {
  return (channel: { name: string }) => ({
    authorize: (socketId: string, callback: (error: boolean, data?: unknown) => void) => {
      void axios
        .post('/broadcasting/auth', {
          socket_id: socketId,
          channel_name: channel.name,
        })
        .then(response => {
          callback(false, response.data)
        })
        .catch((error: unknown) => {
          callback(true, error)
        })
    },
  })
}

function disconnectEcho(): void {
  echoClient?.disconnect()
  echoClient = null
  activeProgramChannelUuid = null
  activePatientCaseChannelUuid = null
}

interface PusherLike {
  connection: {
    state: string
    socket_id?: string
    bind: (ev: string, cb: (...args: unknown[]) => void) => void
  }
}

function bindPusherDiagnostics(pusher: PusherLike): void {
  debugEcho('initial WebSocket state', pusher.connection.state)

  pusher.connection.bind('state_change', (...args: unknown[]) => {
    const states = args[0] as { previous: string; current: string }

    debugEcho('WebSocket state_change', states.previous, '->', states.current)
  })

  pusher.connection.bind('connected', () => {
    debugEcho('WebSocket connected', { socket_id: pusher.connection.socket_id })
  })

  pusher.connection.bind('error', (err: unknown) => {
    console.error('[vector:echo] WebSocket error', err)
  })

  pusher.connection.bind('unavailable', () => {
    debugEcho('WebSocket unavailable')
  })
}

function attachPrivateUserChannelListeners(
  client: Echo<'reverb'>,
  userUuid: string,
  onInboxUpdated: () => void,
): void {
  const channel = client.private(`App.Models.User.${userUuid}`)

  if (import.meta.env.DEV) {
    channel
      .subscribed(() => {
        debugEcho('private channel subscription succeeded', `App.Models.User.${String(userUuid).slice(0, 8)}…`)
      })
      .error((status: unknown) => {
        console.error('[vector:echo] private channel subscription failed (check POST /api/broadcasting/auth)', status)
      })
    channel.listenToAll((event: string, data: unknown) => {
      debugEcho('channel event (raw)', event, data)
    })
  }

  channel.listen('.inbox.updated', (payload: InboxUpdatedPayload) => {
    debugEcho('inbox.updated', payload)
    if (payload?.v !== 1)
      return
    onInboxUpdated()
  })

  channel.listen('.resource.updated', (payload: ResourceUpdatedPayload) => {
    debugEcho('resource.updated', payload)
    dispatchResourceUpdatedSideEffects(payload)
  })
}

function attachPrivateProgramChannelListeners(
  client: Echo<'reverb'>,
  programUuid: string,
): void {
  const channel = client.private(`vector.resource.program.${programUuid}`)

  if (import.meta.env.DEV) {
    channel
      .subscribed(() => {
        debugEcho('program channel subscription succeeded', `vector.resource.program.${String(programUuid).slice(0, 8)}…`)
      })
      .error((status: unknown) => {
        console.error('[vector:echo] program channel subscription failed', status)
      })
  }

  channel.listen('.resource.updated', (payload: ResourceUpdatedPayload) => {
    debugEcho('program channel resource.updated', payload)
    dispatchResourceUpdatedSideEffects(payload)
  })
}

function attachPrivatePatientCaseChannelListeners(
  client: Echo<'reverb'>,
  patientCaseUuid: string,
): void {
  const channel = client.private(`vector.resource.patient_case.${patientCaseUuid}`)

  if (import.meta.env.DEV) {
    channel
      .subscribed(() => {
        debugEcho('patient-case channel subscription succeeded', `vector.resource.patient_case.${String(patientCaseUuid).slice(0, 8)}…`)
      })
      .error((status: unknown) => {
        console.error('[vector:echo] patient-case channel subscription failed', status)
      })
  }

  channel.listen('.resource.updated', (payload: ResourceUpdatedPayload) => {
    debugEcho('patient-case channel resource.updated', payload)
    dispatchResourceUpdatedSideEffects(payload)
  })
}

/** Route params may be string | string[]; only single string segments are subscribed. */
function normalizeRouteUuidParam(value: unknown): string | null {
  if (typeof value !== 'string')
    return null
  const t = value.trim()

  return t === '' ? null : t
}

function syncProgramChannelSubscription(client: Echo<'reverb'>, nextProgramUuid: string | null): void {
  if (activeProgramChannelUuid && activeProgramChannelUuid !== nextProgramUuid)
    client.leave(`private-vector.resource.program.${activeProgramChannelUuid}`)

  if (!nextProgramUuid) {
    activeProgramChannelUuid = null

    return
  }

  if (activeProgramChannelUuid === nextProgramUuid)
    return

  attachPrivateProgramChannelListeners(client, nextProgramUuid)
  activeProgramChannelUuid = nextProgramUuid
}

function syncPatientCaseChannelSubscription(client: Echo<'reverb'>, nextPatientCaseUuid: string | null): void {
  if (activePatientCaseChannelUuid && activePatientCaseChannelUuid !== nextPatientCaseUuid)
    client.leave(`private-vector.resource.patient_case.${activePatientCaseChannelUuid}`)

  if (!nextPatientCaseUuid) {
    activePatientCaseChannelUuid = null

    return
  }

  if (activePatientCaseChannelUuid === nextPatientCaseUuid)
    return

  attachPrivatePatientCaseChannelListeners(client, nextPatientCaseUuid)
  activePatientCaseChannelUuid = nextPatientCaseUuid
}

/**
 * Returns the active Echo client instance, or null if not yet connected.
 * Intended for use by other composables that need to subscribe to additional channels
 * on the same Echo connection (e.g. useSsdiActiveCall).
 */
export function getEchoClient(): Echo<'reverb'> | null {
  return echoClient
}

/**
 * Laravel Reverb + Echo private user channel, REST inbox hydration for Vector users,
 * `inbox.updated` refetches the inbox; `resource.updated` hints at domain changes (separate from inbox rows).
 */
export function useUserInboxNotificationsInit(): void {
  const auth = useAuthStore()
  const user = useUserStore()
  const route = useRoute()
  const inbox = useUserInboxStore()
  const { isLoggedIn } = storeToRefs(auth)

  let refetchTimer: ReturnType<typeof setTimeout> | null = null

  function clearRefetchTimer(): void {
    if (refetchTimer !== null) {
      clearTimeout(refetchTimer)
      refetchTimer = null
    }
  }

  function maybeRefetchInbox(): void {
    if (!isLoggedIn.value || !user.data?.access?.vector)
      return
    clearRefetchTimer()
    refetchTimer = setTimeout(() => {
      refetchTimer = null
      if (!isLoggedIn.value || !user.data?.access?.vector)
        return
      debugEcho('debounced refetch firing')
      void inbox.fetchListFromRealtimeSignal()
    }, 1500)
  }

  async function syncEcho(): Promise<void> {
    const run = ++syncEchoRunId

    disconnectEcho()

    const appKey = import.meta.env.VITE_REVERB_APP_KEY
    const serverUrl = import.meta.env.VITE_SERVER_URL
    const serverBaseUrl = serverUrl?.replace(/\/$/, '').replace(/\/api$/, '')

    if (!isLoggedIn.value) {
      debugEcho('skip: not logged in')

      return
    }
    if (!appKey) {
      debugEcho('skip: VITE_REVERB_APP_KEY is empty — add it to client .env and restart Vite')

      return
    }
    if (!serverUrl) {
      debugEcho('skip: VITE_SERVER_URL is empty')

      return
    }

    const userUuid = user.data?.uuid

    if (!userUuid) {
      debugEcho('profile uuid missing; waiting for user store hydration')

      return
    }

    if (run !== syncEchoRunId)
      return

    if (!serverBaseUrl) {
      debugEcho('skip: could not derive API base URL from VITE_SERVER_URL')

      return
    }

    try {
      await ensureCsrfCookieForEcho(serverBaseUrl)
    } catch (error) {
      console.error('[vector:echo] failed to refresh Sanctum CSRF cookie before Echo connect', error)

      return
    }

    if (run !== syncEchoRunId)
      return

    const scheme = import.meta.env.VITE_REVERB_SCHEME ?? (import.meta.env.DEV ? 'http' : 'https')
    const tls = scheme === 'https'
    const port = Number(import.meta.env.VITE_REVERB_PORT ?? (tls ? 443 : 80))
    const wsHost = import.meta.env.VITE_REVERB_HOST ?? window.location.hostname

    debugEcho('connecting Echo', {
      wsHost,
      port,
      scheme,
      forceTLS: tls,
      authVia: 'axios /broadcasting/auth',
      userUuidPrefix: `${String(userUuid).slice(0, 8)}…`,
    })

    window.Pusher = Pusher

    echoClient = new Echo({
      broadcaster: 'reverb',
      key: appKey,
      wsHost,
      wsPort: port,
      wssPort: port,
      forceTLS: tls,
      enabledTransports: ['ws', 'wss'],
      authorizer: createEchoChannelAuthorizer(),
    })

    if (run !== syncEchoRunId) {
      disconnectEcho()

      return
    }

    const connector = echoClient.connector as { pusher?: PusherLike }

    const pusher = connector.pusher
    if (!pusher) {
      debugEcho('BUG: Echo connector has no pusher instance')
      disconnectEcho()

      return
    }

    bindPusherDiagnostics(pusher)
    attachPrivateUserChannelListeners(echoClient, userUuid, maybeRefetchInbox)

    const routeProgramUuid = normalizeRouteUuidParam(route.params?.program_uuid)
    const routePatientCaseUuid = normalizeRouteUuidParam(route.params?.case_id)

    syncProgramChannelSubscription(echoClient, routeProgramUuid)
    syncPatientCaseChannelSubscription(echoClient, routePatientCaseUuid)
  }

  watch(
    [isLoggedIn, () => user.data?.access?.vector, () => user.data?.uuid],
    () => {
      if (!isLoggedIn.value || !user.data?.access?.vector) {
        clearRefetchTimer()
        inbox.clearLocal(!isLoggedIn.value)

        return
      }
      void inbox.fetchList()
    },
    { immediate: true },
  )

  watch([isLoggedIn, () => user.data?.uuid], () => {
    void syncEcho()
  }, { immediate: true })

  watch(
    [() => route.params?.program_uuid, () => route.params?.case_id],
    () => {
      if (!echoClient) {
        activeProgramChannelUuid = null
        activePatientCaseChannelUuid = null

        return
      }
      const routeProgramUuid = normalizeRouteUuidParam(route.params?.program_uuid)
      const routePatientCaseUuid = normalizeRouteUuidParam(route.params?.case_id)

      syncProgramChannelSubscription(echoClient, routeProgramUuid)
      syncPatientCaseChannelSubscription(echoClient, routePatientCaseUuid)
    },
    { immediate: true },
  )

  onUnmounted(() => {
    clearRefetchTimer()
    disconnectEcho()
  })
}
